import { AutofocusDirective } from '../../../../../../shared/directive/autofocus.directive';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDatepickerMaskDirective } from '@anihis/shared/src/shared/directive/mat-datepicker-mask.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getMaxBirthDate,
  getMinAnimalBirthDate,
} from '@anihis/shared/src/validators/birth-date.validators';

function arrayRequiredValidator(control: AbstractControl) {
  const v = control.value;
  if (Array.isArray(v)) return v.length > 0 ? null : { required: true };
  return v ? null : { required: true };
}

export interface ReferralForHistopathologyData {
  referralInfo: {
    date: string | null;
    time: string | null;
    referringClinic: string | null;
    referringDoctor: string | null;
    other: string | null;
    distributionNumber1: string | null;
    distributionComment1: string | null;
    distributionNumber2: string | null;
    distributionComment2: string | null;
    distributionNumber3: string | null;
    distributionComment3: string | null;
    tissueLocation: string[] | null;
    tissueOther: string | null;
    dimensions: string | null;
    morphology: string | null;
    tissueConsistency: string[] | null;
    tissueConsistencyOther: string | null;
    color: string | null;
    duration: string | null;
    regionalLymphNodes: string | null;
    otherTumors: string | null;
    firstOccurrence: string | null;
    lesionRemovedEntirely: string | null;
    veterinarianName: string | null;
  };
  sample: {
    sampleType: string | null;
    sampleLocation: string | null;
    sampleId: string | null;
  };
  clinicalDetails: {
    clinicalHistory: string | null;
    macroscopicDescription: string | null;
    specialInstructions: string | null;
  };
  contact: {
    phone: string | null;
    email: string | null;
  };
}

export interface ReferralForHistopathologyPayload
  extends ReferralForHistopathologyData {
  referralInfo: ReferralForHistopathologyData['referralInfo'] & {
    sampledAt?: string;
  };
}

@Component({
  selector: 'anihis-referral-for-histopathology',
  standalone: true,
  imports: [
    AutofocusDirective,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatDatepickerMaskDirective,
    MatNativeDateModule,
    MatButtonModule,
    TranslocoModule,
  ],
  templateUrl: './referral-for-histopathology.component.html',
  styleUrls: ['./referral-for-histopathology.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralForHistopathologyComponent implements OnInit {
  minDate = getMinAnimalBirthDate();
  today = getMaxBirthDate();

  @Output() formChange = new EventEmitter<ReferralForHistopathologyData>();
  @Output() save = new EventEmitter<ReferralForHistopathologyPayload>();

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  form!: FormGroup;

  constructor() {
    this.form = this.fb.group({
      referralInfo: this.fb.group({
        date: new FormControl('', [Validators.required]),
        time: new FormControl('', [Validators.required]),
        referringClinic: new FormControl(''),
        referringDoctor: new FormControl(''),
        other: new FormControl('', [Validators.required]),
        distributionNumber1: new FormControl(''),
        distributionComment1: new FormControl(''),
        distributionNumber2: new FormControl(''),
        distributionComment2: new FormControl(''),
        distributionNumber3: new FormControl(''),
        distributionComment3: new FormControl(''),
        tissueLocation: new FormControl<string[]>([], [arrayRequiredValidator]),
        tissueOther: new FormControl(''),
        dimensions: new FormControl('', [Validators.required]),
        morphology: new FormControl('', [Validators.required]),
        tissueConsistency: new FormControl<string[]>(
          [],
          [arrayRequiredValidator],
        ),
        tissueConsistencyOther: new FormControl(''),
        color: new FormControl('', [Validators.required]),
        duration: new FormControl('', [Validators.required]),
        regionalLymphNodes: new FormControl<string | null>(null, [
          Validators.required,
        ]),
        otherTumors: new FormControl<string | null>(null, [
          Validators.required,
        ]),
        firstOccurrence: new FormControl<string | null>(null, [
          Validators.required,
        ]),
        lesionRemovedEntirely: new FormControl<string | null>(null, [
          Validators.required,
        ]),
        veterinarianName: new FormControl(''),
      }),
      sample: this.fb.group({
        sampleType: new FormControl(''),
        sampleLocation: new FormControl(''),
        sampleId: new FormControl(''),
      }),
      clinicalDetails: this.fb.group({
        clinicalHistory: new FormControl(''),
        macroscopicDescription: new FormControl(''),
        specialInstructions: new FormControl(''),
      }),
      contact: this.fb.group({
        phone: new FormControl(''),
        email: new FormControl(''),
      }),
    });
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.formChange.emit(value);
      });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.save.emit(this.getSavePayload());
    }
  }

  private getSavePayload(): ReferralForHistopathologyPayload {
    const v = this.form.getRawValue();
    const payload = JSON.parse(
      JSON.stringify(v),
    ) as ReferralForHistopathologyPayload;
    if (!payload.referralInfo) payload.referralInfo = {} as any;

    const date = payload.referralInfo.date;
    const time = payload.referralInfo.time;

    if (date) {
      const d = new Date(date);
      if (!isNaN(d.getTime()) && time) {
        const [hh, mm] = ('' + time)
          .split(':')
          .map((s: string) => parseInt(s, 10));
        if (!Number.isNaN(hh)) d.setHours(hh);
        if (!Number.isNaN(mm)) d.setMinutes(mm);
        payload.referralInfo.sampledAt = d.toISOString();
      }
    }

    return payload;
  }
}
